<template>
    <div class="form-wrapper">
        <h1>Generate Report</h1>
        <p>Analyze a field's value distribution across submissions</p>

        <div class="alert alert-danger p-0" v-if="error">{{ error }}</div>

        <form action="#" @submit.prevent="submit" class="new-project-form">
            <div class="fields-wrapper">

                <!-- Period -->
                <div class="form-group">
                    <label>Period</label>
                    <select class="form-control" v-model="period">
                        <option value="all">All submissions</option>
                        <option value="monthly">Monthly</option>
                        <option value="annually">Annually</option>
                        <option value="custom">Custom range</option>
                    </select>
                </div>

                <!-- Monthly selector -->
                <div class="row" v-if="period === 'monthly'">
                    <div class="col-8">
                        <div class="form-group">
                            <label>Month</label>
                            <select class="form-control" v-model="month" :disabled="loadingPeriods">
                                <option value="" disabled>{{ loadingPeriods ? 'Loading...' : 'Select a month' }}</option>
                                <option v-for="m in availableMonths" :key="m.value" :value="m.value">{{ m.label }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Annually selector -->
                <div class="row" v-if="period === 'annually'">
                    <div class="col-8">
                        <div class="form-group">
                            <label>Year</label>
                            <select class="form-control" v-model="year" :disabled="loadingPeriods">
                                <option value="" disabled>{{ loadingPeriods ? 'Loading...' : 'Select a year' }}</option>
                                <option v-for="y in availableYears" :key="y.value" :value="y.value">{{ y.label }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Custom range -->
                <div class="row" v-if="period === 'custom'">
                    <div class="col-6">
                        <div class="form-group">
                            <label>Start Date</label>
                            <input type="date" class="form-control" v-model="startDate">
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="form-group">
                            <label>End Date</label>
                            <input type="date" class="form-control" v-model="endDate">
                        </div>
                    </div>
                </div>

                <!-- Load Fields Button -->
                <div class="form-group" v-if="!fieldsLoaded">
                    <a href="#" class="action-btn" @click.prevent="loadFields" v-if="!loadingFields">
                        Load Available Fields
                    </a>
                    <a href="#" class="action-btn disabled" v-if="loadingFields">
                        <span class="spinner-border spinner-border-sm mr-1"></span>
                        Loading...
                    </a>
                    <small class="form-text text-muted mt-1 d-block">Fields with 20 or fewer unique values will be available for analysis.</small>
                </div>

                <!-- Field Selection -->
                <div class="form-group" v-if="fieldsLoaded">
                    <label>Field to Analyze</label>
                    <select class="form-control" v-model="selectedField">
                        <option value="" disabled>Select a field</option>
                        <option v-for="f in analyzableFields" :key="f.field" :value="f.field">
                            {{ f.field }} ({{ f.unique_values }} unique values)
                        </option>
                    </select>
                    <small class="form-text text-muted mt-1" v-if="analyzableFields.length === 0">
                        No analyzable fields found for this period. Fields must have 20 or fewer unique values.
                    </small>
                    <small class="form-text text-muted mt-1" v-if="totalSubmissions > 0">
                        {{ totalSubmissions }} submissions found for this period.
                    </small>
                </div>

                <div class="mt-4">
                    <button type="submit" class="btn btn-primary" style="width: 180px" :disabled="submitting || !selectedField">
                        <span class="spinner-border" v-if="submitting"></span>
                        <span :class="{'opacity-0': submitting}">Generate Report</span>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import repository from "@/repository/repository";

export default {
    name: "GenerateReportModal",
    data() {
        return {
            period: "all",
            month: "",
            year: "",
            startDate: "",
            endDate: "",
            loadingPeriods: false,
            availableMonths: [],
            availableYears: [],
            periodsLoaded: false,
            loadingFields: false,
            fieldsLoaded: false,
            analyzableFields: [],
            totalSubmissions: 0,
            selectedField: "",
            submitting: false,
            error: null,
        }
    },
    methods: {
        loadPeriods() {
            if (this.periodsLoaded || this.loadingPeriods) return;
            this.loadingPeriods = true;
            repository.get("/projects/" + this.projectId + "/forms/" + this.formId + "/submissions/periods")
                .then(response => {
                    this.availableMonths = response.data.months;
                    this.availableYears = response.data.years;
                    this.periodsLoaded = true;
                    this.loadingPeriods = false;
                })
                .catch(() => {
                    this.loadingPeriods = false;
                })
        },
        loadFields() {
            if (this.loadingFields) return;
            this.error = null;

            // Validate period selection
            if (this.period === 'monthly' && !this.month) {
                this.error = "Please select a month first.";
                return;
            }
            if (this.period === 'annually' && !this.year) {
                this.error = "Please select a year first.";
                return;
            }
            if (this.period === 'custom') {
                if (!this.startDate || !this.endDate) {
                    this.error = "Both start date and end date are required.";
                    return;
                }
                if (this.startDate > this.endDate) {
                    this.error = "Start date must be before or equal to end date.";
                    return;
                }
            }

            this.loadingFields = true;

            let params = { period: this.period };
            if (this.period === 'monthly') params.period_month = this.month;
            if (this.period === 'annually') params.period_year = this.year;
            if (this.period === 'custom') {
                params.period_start = this.startDate;
                params.period_end = this.endDate;
            }

            repository.get("/projects/" + this.projectId + "/forms/" + this.formId + "/reports/fields", {
                params: params,
                timeout: 60000,
            })
                .then(response => {
                    this.analyzableFields = response.data.fields;
                    this.totalSubmissions = response.data.total_submissions;
                    this.fieldsLoaded = true;
                    this.loadingFields = false;
                })
                .catch(() => {
                    this.error = "Failed to load analyzable fields.";
                    this.loadingFields = false;
                })
        },
        submit() {
            if (this.submitting || !this.selectedField) return;
            this.error = null;
            this.submitting = true;

            let data = {
                field: this.selectedField,
                period: this.period,
            };
            if (this.period === 'monthly') data.period_month = this.month;
            if (this.period === 'annually') data.period_year = this.year;
            if (this.period === 'custom') {
                data.period_start = this.startDate;
                data.period_end = this.endDate;
            }

            repository.post("/projects/" + this.projectId + "/forms/" + this.formId + "/reports", data)
                .then(() => {
                    this.submitting = false;
                    this.$emit('created');
                    this.$emit('close');
                })
                .catch((err) => {
                    this.error = (err.response && err.response.data && err.response.data.message) || "Failed to create report.";
                    this.submitting = false;
                })
        },
        reset() {
            this.period = "all";
            this.month = "";
            this.year = "";
            this.startDate = "";
            this.endDate = "";
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.totalSubmissions = 0;
            this.selectedField = "";
            this.submitting = false;
            this.error = null;
        },
    },
    computed: {
        formId() {
            return this.$route.params.id;
        },
        projectId() {
            return this.$store.getters.currentProject.hashId;
        },
    },
    watch: {
        period(val) {
            // Reset fields when period changes
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.selectedField = "";
            if (val === 'monthly' || val === 'annually') {
                this.loadPeriods();
            }
        },
        month() {
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.selectedField = "";
        },
        year() {
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.selectedField = "";
        },
        startDate() {
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.selectedField = "";
        },
        endDate() {
            this.fieldsLoaded = false;
            this.analyzableFields = [];
            this.selectedField = "";
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.opacity-0 {
    opacity: 0;
}

.action-btn {
    display: inline-block;
    padding: 0.4rem 0.75rem;
    background: $background-grey;
    border-radius: $box-border-radius;
    border: 1px solid transparent;
    text-decoration: none;
    color: $dark;
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
    @extend .animated;

    &:hover {
        background: lighten($primary, 50%);
        color: $primary;
    }

    &.disabled {
        pointer-events: none;
        opacity: 0.7;
    }

    .spinner-border-sm {
        width: 0.85rem;
        height: 0.85rem;
        border-width: 2px;
    }
}
</style>
